<template>
  <aside
    v-if="!isCollapsed"
    class="context-panel border-l"
    style="width: 280px; background: #FAFAFA; border-color: var(--border-light);"
  >
    <!-- Header -->
    <div class="px-4 py-3 border-b flex items-center justify-between"
         style="border-color: var(--border-light); background: var(--sage-green-light, #E8EEE8);">
      <h3 class="text-sm font-semibold" style="color: var(--text-primary);">
        {{ panelTitle }}
      </h3>
      <Button
        icon="pi pi-times"
        text
        rounded
        size="small"
        @click="togglePanel"
        class="text-gray-500 hover:text-gray-700"
      />
    </div>

    <!-- Content -->
    <div class="overflow-y-auto" style="height: calc(100vh - 120px);">
      <!-- Table of Contents -->
      <div v-if="tocItems.length > 0" class="px-4 py-3">
        <nav class="space-y-1">
          <a
            v-for="item in tocItems"
            :key="item.id"
            :href="`#${item.id}`"
            :class="[
              'block py-1.5 text-sm hover:text-blue-600 transition-colors',
              item.level === 2 ? 'font-medium' : 'pl-4 text-xs',
              activeHeaderId === item.id ? 'text-blue-600 font-semibold' : 'text-gray-600'
            ]"
            @click.prevent="scrollToSection(item.id)"
          >
            {{ item.text }}
          </a>
        </nav>
      </div>

      <!-- Empty State -->
      <div v-if="tocItems.length === 0"
           class="px-4 py-8 text-center">
        <i class="pi pi-list text-3xl mb-2" style="color: var(--text-tertiary);"></i>
        <p class="text-sm" style="color: var(--text-tertiary);">
          No outline available
        </p>
      </div>
    </div>
  </aside>

  <!-- Collapsed Toggle Button -->
  <div
    v-else
    class="border-l flex items-start pt-3"
    style="background: #FAFAFA; border-color: var(--border-light); width: 48px;"
  >
    <Button
      icon="pi pi-angle-left"
      text
      rounded
      size="small"
      @click="togglePanel"
      class="text-gray-500 hover:text-gray-700 mx-auto"
      v-tooltip.left="'Show outline'"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import Button from 'primevue/button';
import Tooltip from 'primevue/tooltip';
import { parseMarkdownHeaders, scrollToHeader, type TocItem } from '@/utils/markdown';
import type { Section } from '@/types/ashmatics';

// Register tooltip directive
const vTooltip = Tooltip;

interface Props {
  section: Section | null;
  markdownContent?: string;
  showToc?: boolean; // Whether to show TOC (false for component-driven tabs)
}

const props = withDefaults(defineProps<Props>(), {
  showToc: true
});

// State
const isCollapsed = ref(false);
const activeHeaderId = ref<string | null>(null);

// Computed
const panelTitle = computed(() => {
  return 'On This Page';
});

const tocItems = computed<TocItem[]>(() => {
  // Don't parse TOC if showToc is false (component-driven tabs)
  if (!props.showToc || !props.markdownContent) return [];
  return parseMarkdownHeaders(props.markdownContent);
});

// Methods
const togglePanel = () => {
  isCollapsed.value = !isCollapsed.value;
};

const scrollToSection = (headerId: string) => {
  activeHeaderId.value = headerId;
  scrollToHeader(headerId);
};

// Watch for scroll events to highlight active section
// This is a simplified version - you can enhance it with Intersection Observer
watch(
  () => props.section,
  () => {
    activeHeaderId.value = null;
  }
);
</script>

<style scoped>
.context-panel {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.context-panel a {
  text-decoration: none;
  display: block;
  border-left: 2px solid transparent;
  padding-left: 0.75rem;
  margin-left: -0.75rem;
}

.context-panel a:hover {
  border-left-color: var(--primary-gold);
}

.context-panel a.text-blue-600 {
  border-left-color: var(--primary-gold);
}
</style>
